// src/utils/encryption.ts
import pako from 'pako';

const normalizeBase64 = (s: string) => {
  // If it came from querystring parsing, '+' often becomes ' '
  s = s.replace(/ /g, '+');

  // Support base64url variants too
  s = s.replace(/-/g, '+').replace(/_/g, '/');

  // Pad to multiple of 4
  const pad = s.length % 4;
  if (pad) s += '='.repeat(4 - pad);

  return s;
};
const utf8Encode = (str: string): Uint8Array => {
  return new TextEncoder().encode(str);
};

// IMPORTANT: Must match React Native's utf8Decode exactly!
const utf8Decode = (byteArray: Uint8Array): string => {
  let result = '';
  for (let i = 0; i < byteArray.length; i++) {
    result += String.fromCharCode(byteArray[i]);
  }
  return decodeURIComponent(escape(result));
};

const base64Encode = (bytes: Uint8Array): string => {
  let binary = '';
  const len = bytes.byteLength;
  for (let i = 0; i < len; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
};

const base64Decode = (base64String: string): Uint8Array => {
  const normalized = normalizeBase64(base64String);
  const binaryString = atob(normalized);
  const len = binaryString.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i++) bytes[i] = binaryString.charCodeAt(i);
  return bytes;
};

const compress = (data: Uint8Array): Uint8Array => {
  try {
    const compressed = pako.gzip(data, {
      level: 9,
      windowBits: 15,
      memLevel: 8,
    });
    return compressed;
  } catch (error) {
    console.error('Could Not Compress Data', error);
    throw error;
  }
};

const decompress = (byteArray: Uint8Array): Uint8Array => {
  return pako.ungzip(byteArray);
};

export interface ArticleShareData {
  articleId: number;
  mainClassificationName: string;
  relevantFromDate: string;
  articleHeader: string;
  isHeader: boolean;
}

export const encryptArticleData = (data: ArticleShareData): string => {
  try {
    const stringified = JSON.stringify(data);
    const utf8Bytes = utf8Encode(stringified);
    const stageOne = base64Encode(utf8Bytes);
    const stageTwoBytes = utf8Encode(stageOne);
    const compressed = compress(stageTwoBytes);
    const finalBase64 = base64Encode(compressed);
    return `A${finalBase64}`;
  } catch (error) {
    console.error('Could Not Encrypt Data', error);
    throw error;
  }
};

export const decryptArticleData = (encryptedValue: string): ArticleShareData | null => {
  try {
    // Remove the leading "A" character
    if (!encryptedValue.startsWith('A')) {
      console.error('Invalid encrypted ID format');
      return null;
    }
    const withoutPrefix = encryptedValue.substring(1);
    console.log("1 - After removing A");

    // Decode the base64 string to a byte array
    const compressed = base64Decode(withoutPrefix);
    console.log("2 - After base64 decode");

    // Decompress the byte array
    const decompressed = decompress(compressed);
    console.log("3 - After decompress");

    // Decode the decompressed byte array to a base64 string
    const decryptedBase64 = utf8Decode(decompressed);
    console.log("4 - After utf8 decode:", decryptedBase64);

    // Decode the final base64 string to the original string
    const decryptedString = utf8Decode(base64Decode(decryptedBase64));
    console.log("5 - Final decrypted string:", decryptedString);

    // Parse JSON
    const data = JSON.parse(decryptedString);

    if (
      typeof data.articleId === 'number' &&
      typeof data.mainClassificationName === 'string' &&
      typeof data.relevantFromDate === 'string' &&
      typeof data.articleHeader === 'string' &&
      typeof data.isHeader === 'boolean'
    ) {
      return data;
    }
    return null;
  } catch (error) {
    console.error('Could Not Decrypt Value', error);
    return null;
  }
};